import React, { useState, useEffect } from 'react';
import { propOr, path, isEmpty } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  Card,
  ButtonGroup,
  Form,
  FormLayout,
  Frame,
  Layout,
  Modal,
  TextContainer,
  Badge,
  Page,
  TextField,
  RangeSlider,
  Select,
  Toast,
  Heading,
} from '@shopify/polaris';
import SuccessBanner from '../components/SuccessBanner';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { CREATE_SHOP_CAREER_PORTAL, UPDATE_SHOP_CAREER_PORTAL } from '../apollo/mutation';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY_OPTIONS, FONT_CASING_OPTIONS } from '../../utils/formDefaultValues';
import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultGrayColor, defaultDarkGrayColor, hslaColor } from '../../utils/defaultColors';
import { keysToCamel } from '../../utils/normalizer';

const IntroParagraphSection = ({ history, shopifyToken, shopifyDomain }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  //const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;
  const [careerPortalTime, setCareerPortalTime] = useState();
  const [form, setForm] = useState({});
  const [preview, setPreview] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const togglePreviewModal = () => setPreview(!preview);
  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const introductionHeading = propOr('', 'introductionHeading')(careerPortal);
    const introductionBody = propOr('', 'introductionBody')(careerPortal);
    const introductionParagraph = propOr(false, 'introductionParagraph')(careerPortal);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const paragraphSection = propOr({}, 'paragraph_section')(styleControls);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);
    setCareerPortalTime(careerPortal.pageTimestamps);

    setForm((prevForm) => ({
      ...prevForm,
      introductionHeading,
      introductionBody,
      introductionParagraph,
      ...styleControlsFormatted,
      paragraphSection: {
        ...prevForm.paragraphSection,
        backgroundColor: paragraphSection?.background_color || defaultWhiteColor,
        bodyColor: paragraphSection?.body_color || defaultGrayColor,
        headingColor: paragraphSection?.heading_color || defaultDarkGrayColor,
        headingWeight: paragraphSection?.heading_weight || '700',
        headingSize: paragraphSection?.heading_size || '26',
        headingFontFamily: paragraphSection?.heading_font_family || 'inherit',
        headingTextCasing: paragraphSection?.heading_text_casing || 'uppercase',
        bodyWeight: paragraphSection?.body_weight || '200',
        bodySize: paragraphSection?.body_size || '16',
        bodyFontFamily: paragraphSection?.body_font_family || 'inherit',
        topSpacing: paragraphSection?.top_spacing || '80',
        bottomSpacing: paragraphSection?.bottom_spacing || '80',
      },
      talentCommunityStyles: {
        ...styleControlsFormatted?.talentCommunitySection,
      },
      talentCommunitySection,
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const updateField = (fieldName) => (value) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const updateColor = (fieldName) => (color) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      paragraphSection: {
        ...form.paragraphSection,
        [fieldName]: color,
      },
    });
  };

  const updateFontValue =
    ({ sectionName, fieldName }) =>
    (value) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: `${value}`,
        },
      });
    };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setIsSaveBarActive(false);
    setShowSuccessBanner(true);
    setIsLoading(false);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit =
    (publish = true) =>
    (e) => {
      e?.preventDefault();
      setPreview(false);
      setConfirmModal(false);
      setIsLoading(true);
      const careerPortal = propOr({}, 'careerPortal')(data);
      const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
      const variables = {
        ...careerPortal,
        ...form,
        shopifyToken,
        introductionParagraph: publish,
      };
      delete variables.styleControls;

      save({ variables }).then(onSuccess).catch(onError);
    };

  const filterPageTimeLabel = (label) => {
    if (!careerPortalTime) return;
    return careerPortalTime.filter((val) => val.attribute == label)[0].label;
  };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;
  //const isSubscribedUser = !isEmpty(currentSubscription);

  return (
    <AppProvider
      i18n={{
        Polaris: {
          DropZone: {
            actionTitle: 'Add image',
          },
        },
      }}>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { changeRoute('/career-portal-index'); } }]}
          title='Introductory paragraph section'
          subtitle={filterPageTimeLabel('introduction_paragraph')}
          titleMetadata={
            <Badge status={form.introductionParagraph ? 'success' : 'default'}>
              {form.introductionParagraph ? 'Published' : 'Unpublished'}
            </Badge>
          }
          pagination={{
            hasPrevious: !isSaveBarActive,
            hasNext: !isSaveBarActive,
            nextURL: !isSaveBarActive ? `/career-portal/employee-testimonials-section` : null,
            previousURL: !isSaveBarActive ? `/career-portal/hero-section` : null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button monochrome plain onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button
                primary
                disabled={!isSaveBarActive && form.introductionParagraph}
                onClick={onSubmit(true)}
                connectedDisclosure={
                  form.introductionParagraph
                    ? {
                        accessibilityLabel: 'Change section status',
                        actions: [{ content: 'Unpublish', onAction: toggleConfirmModal }],
                      }
                    : undefined
                }>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit()}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Introductory paragraph section updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={`https://${shopifyDomain}/a/careers/`}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection
                title='Content'
                description={
                  <>
                    <p style={{ marginBottom: '10px' }}>
                      Let everyone know what your company is all about with a couple sentences explaining why they should work
                      there.
                    </p>
                  </>
                }>
                <Card sectioned>
                  <FormLayout>
                    <TextField
                      id='introductionHeading'
                      label='Title text'
                      value={form.introductionHeading}
                      name='introductionHeading'
                      onChange={updateField('introductionHeading')}
                    />
                    <TextField
                      id='introductionBody'
                      label='Paragraph text'
                      value={form.introductionBody}
                      name='introductionBody'
                      multiline={5}
                      onChange={updateField('introductionBody')}
                    />
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Layout'
                description='Customize the content alignment and spacing to match your store’s theme.'>
                <Card sectioned>
                  <FormLayout>
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Top spacing'
                        value={form.paragraphSection?.topSpacing}
                        onChange={updateFontValue({ sectionName: 'paragraphSection', fieldName: 'topSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.paragraphSection?.topSpacing}px`}
                      />
                      <RangeSlider
                        label='Bottom spacing'
                        value={form.paragraphSection?.bottomSpacing}
                        onChange={updateFontValue({ sectionName: 'paragraphSection', fieldName: 'bottomSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.paragraphSection?.bottomSpacing}px`}
                      />
                    </div>
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and section background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='SECTION'>
                    <ColorPicker
                      colorName='Background color'
                      defaultColor={form.paragraphSection?.backgroundColor}
                      onSelectColor={updateColor('backgroundColor')}
                    />
                  </Card.Section>
                  <Card.Section title='TITLE TEXT'>
                    <ColorPicker
                      colorName='Title color'
                      defaultColor={form.paragraphSection?.headingColor}
                      onSelectColor={updateColor('headingColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Title font size'
                        value={form.paragraphSection?.headingSize}
                        onChange={updateFontValue({ sectionName: 'paragraphSection', fieldName: 'headingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.paragraphSection?.headingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'paragraphSection', fieldName: 'headingFontFamily' })}
                        value={form.paragraphSection?.headingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'paragraphSection', fieldName: 'headingWeight' })}
                        value={form.paragraphSection?.headingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'paragraphSection', fieldName: 'headingTextCasing' })}
                        value={form.paragraphSection?.headingTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='PARAGRAPH TEXT'>
                    <ColorPicker
                      colorName='Paragraph color'
                      defaultColor={form.paragraphSection?.bodyColor}
                      onSelectColor={updateColor('bodyColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Paragraph font size'
                        value={form.paragraphSection?.bodySize}
                        onChange={updateFontValue({ sectionName: 'paragraphSection', fieldName: 'bodySize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.paragraphSection?.bodySize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Paragraph font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'paragraphSection', fieldName: 'bodyFontFamily' })}
                        value={form.paragraphSection?.bodyFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Paragraph font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'paragraphSection', fieldName: 'bodyWeight' })}
                        value={form.paragraphSection?.bodyWeight}
                      />
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive && form.introductionParagraph}>
                Publish
              </Button>
            </div>
          </Form>
          <Modal
            open={!!preview}
            large
            onClose={togglePreviewModal}
            primaryAction={{
              content: 'Publish',
              disabled: !isSaveBarActive && form.introductionParagraph,
              onAction: onSubmit(),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: togglePreviewModal,
              },
            ]}
            title={
              <TextContainer>
                <Heading>Preview introductory paragraph section</Heading>
                <p style={{ fontSize: '0.85em' }}>
                  Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
                  section will look on your store
                </p>
              </TextContainer>
            }
            loading={false}>
            <div className={'preview__modal'}>
              <section
                className='paragraph_section'
                style={{
                  backgroundColor: `${hslaColor(form.paragraphSection?.backgroundColor, '#fff')}`,
                }}>
                <div className='wrapper'>
                  <h2
                    className='heading_text'
                    style={{
                      color: `${hslaColor(form.paragraphSection?.headingColor, '#3a3a3a')}`,
                      fontSize: `${form.paragraphSection?.headingSize || '26'}px`,
                      fontWeight: `${form.paragraphSection?.headingWeight || '700'}`,
                    }}>
                    {form.introductionHeading}
                  </h2>
                  <p
                    className='body_content'
                    style={{
                      color: `${hslaColor(form.paragraphSection?.bodyColor, '#3a3a3a')}`,
                      fontSize: `${form.paragraphSection?.bodySize || '16'}px`,
                      fontWeight: `${form.paragraphSection?.bodyWeight || '200'}`,
                    }}>
                    {form.introductionBody}
                  </p>
                </div>
              </section>
            </div>
          </Modal>
          <Modal
            open={confirmModal}
            onClose={toggleConfirmModal}
            title={`${form.introductionParagraph ? 'Unpublish' : 'Publish'} section`}
            primaryAction={{
              content: `Yes, ${form.introductionParagraph ? 'unpublish' : 'publish'} section`,
              onAction: onSubmit(!form.introductionParagraph),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: toggleConfirmModal,
              },
            ]}
            loading={false}>
            <Modal.Section>
              <p>{`Do you want to ${
                form.introductionParagraph ? 'unpublish' : 'publish'
              } this section to your career portal page?`}</p>
            </Modal.Section>
          </Modal>
        </Page>
        {toastMarkup}
      </Frame>
    </AppProvider>
  );
};

export default IntroParagraphSection;
